import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

// Shadcn components
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Switch } from '@/components/shadcn/switch'
import { Button } from '@/components/shadcn/button'
import { SingleSelectComboboxField, SelectOption } from '@/components/form-components/SingleSelectCombobox'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { useCurrentWorkspace } from '../useCurrentWorkspace'
import clsx from 'clsx'

type WorkspaceUserForForm = RestSchemas['Messages.Projects.ProjectUser'] & { authCode?: string }
type MessagesApplicationsApplicationRole = RestSchemas['Messages.Applications.ApplicationRole']
type MessagesWorkspacesMemberRole = RestSchemas['Messages.Workspaces.MemberRole']

// Form validation schema
const formSchema = z.object({
  userId: z.number().min(1, 'User is required'),
  roleToggle: z.record(z.string(), z.boolean()),
})

export type TWorkspaceUsersForm = z.infer<typeof formSchema>

type WorkspaceUsersFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedUser?: WorkspaceUserForForm
}

export const WorkspaceUsersForm = (props: WorkspaceUsersFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent
        {...props}
        onSubmit={() => {
          setSheetOpen(false)
          props.onSubmit?.()
        }}
      />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: WorkspaceUsersFormProps) => {
  const { workspaceId, project_id, app_id, organisation_id } = useCurrentWorkspace()
  // const edit = !!props.selectedUser
  const { selectedUser } = props

  const {
    getApplicationsApiListApplicationRoles,
    getWorkspacesApiGetUsers,
    //postWorkspacesApiAddOrUpdateExternalSubscriptionUser,
    getSubscriptionsApiListOrganisationUsers,
    putProjectsApiAddUserToProject,
    putProjectsApiUpdateUserFromProject,
  } = useRestClient()

  const {
    data: workspaceUsers,
    isPending: usersPending,
    isError: usersError,
  } = getWorkspacesApiGetUsers.useQuery({
    query: { workspaceId },
  })

  const orgUsersSelect = (data: RestSchemas['Messages.Subscriptions.OrganisationUser'][]) => {
    const projectUserIds = new Set((workspaceUsers ?? []).map((user) => user.id))
    return data.filter((user) => user.active && !projectUserIds.has(user.id))
  }

  const {
    data: unassignedOrgUsers,
    isPending: unassignedPending,
    isError: unassignedError,
  } = getSubscriptionsApiListOrganisationUsers.useQuery(
    { query: { orgId: organisation_id } },
    { select: orgUsersSelect, enabled: !!workspaceUsers && !props.selectedUser }
  )

  const {
    data: appRoles,
    isPending: appRolesPending,
    isError: appRolesError,
  } = getApplicationsApiListApplicationRoles.useQuery({
    query: { applicationId: app_id },
  })

  const invalidateWorkspaceUsers = () => {
    getWorkspacesApiGetUsers.invalidateQueries({ parameters: { query: { workspaceId } } })
  }

  const { mutate: addUser } = putProjectsApiAddUserToProject.useMutation()
  const { mutate: updateUser } = putProjectsApiUpdateUserFromProject.useMutation()

  // Prepare roles list
  const roleList = useMemo(() => {
    return ((appRoles as MessagesApplicationsApplicationRole[]) ?? []).map((role) => ({
      id: role.id,
      name: role.name || 'Role',
      description: role.description || '',
    }))
  }, [appRoles])

  const defaultValues = useMemo<TWorkspaceUsersForm>(() => {
    const toggles: Record<string, boolean> = {}
    roleList.forEach((r) => {
      const isAssigned = props.selectedUser?.memberRoles?.some((mr) => mr.roleId === r.id) ?? false
      toggles[String(r.id)] = isAssigned
    })
    return {
      userId: props.selectedUser?.id ?? 0,
      roleToggle: toggles,
    }
  }, [roleList, props.selectedUser])

  // Build user options for combobox
  const userOptions: SelectOption<number>[] = useMemo(() => {
    const base = (unassignedOrgUsers ?? []).map((u) => ({
      value: u.id,
      label: clsx(u.givenName, u.familyName, ` - ( ${u.email} )`),
    }))
    if (props.selectedUser) {
      const selected = {
        value: props.selectedUser.id,
        label: clsx(props.selectedUser.givenName, props.selectedUser.familyName, ` - ( ${props.selectedUser.email} )`),
      }
      // Ensure selected user exists in options so the combobox shows the label
      if (!base.some((o) => o.value === selected.value)) {
        return [selected]
      }
    }
    return base
  }, [unassignedOrgUsers, props.selectedUser])

  const form = useForm<TWorkspaceUsersForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  // Ensure defaults are applied once roles/selectedUser load
  useEffect(() => {
    if (selectedUser) {
      // In edit mode, fully sync defaults including userId and role toggles
      form.reset(defaultValues)
    } else {
      // In add-user mode, preserve current userId but sync role toggles shape/state
      const current = form.getValues()
      form.reset({ ...current, roleToggle: defaultValues.roleToggle })
    }
  }, [defaultValues, selectedUser, form])

  useEffect(() => console.log(userOptions), [userOptions])

  const watchUserId = useWatch({ control: form.control, name: 'userId' })

  if (usersPending || appRolesPending || (unassignedPending && !selectedUser)) return <FormComponentLoading />
  if (usersError || appRolesError || (unassignedError && !selectedUser)) return <ErrorComponent />

  const handleSubmit = (data: TWorkspaceUsersForm) => {
    const assignedRoleIds = Object.entries(data.roleToggle)
      .filter(([, value]) => !!value)
      .map(([key]) => parseInt(key))

    if (assignedRoleIds.length === 0) {
      toast.warning('Please assign at least one role')
      return
    }

    const memberRoles: MessagesWorkspacesMemberRole[] = assignedRoleIds.map((roleId) => ({
      roleId,
      projectId: project_id,
      userId: data.userId,
    }))

    if (selectedUser) {
      const proceedUpdateRoles = () => {
        updateUser(
          { query: { projectId: project_id, userId: selectedUser.id }, body: memberRoles },
          {
            onSuccess: () => {
              toast.success('User updated successfully')
              invalidateWorkspaceUsers()
              props.onSubmit?.()
            },
            onError: () => {
              toast.error('Failed to update user')
            },
          }
        )
      }

      proceedUpdateRoles()
    } else {
      if (!data.userId || data.userId <= 0) {
        toast.error('No user selected')
        return
      }
      addUser(
        { body: memberRoles },
        {
          onSuccess: () => {
            toast.success('User added successfully')
            invalidateWorkspaceUsers()
            props.onSubmit?.()
          },
          onError: () => {
            toast.error('Failed to add user')
          },
        }
      )
    }
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <SingleSelectComboboxField<number, TWorkspaceUsersForm>
                control={form.control}
                name="userId"
                label="User"
                options={userOptions}
                placeholder={selectedUser ? 'Selected User' : 'Select a user'}
                searchPlaceholder="Search users..."
                emptyMessage="No users found."
                disabled={!!selectedUser}
                allowClear={!selectedUser}
              />
              {!!watchUserId && (
                <div className="space-y-2 rounded border bg-background p-5">
                  <div className="text-lg font-medium">Assign Roles</div>
                  {roleList.map((role) => (
                    <FormField
                      key={role.id}
                      control={form.control}
                      name={`roleToggle.${role.id}`}
                      render={({ field }) => (
                        <FormItem className="bg-offwhite flex flex-row items-center justify-between gap-x-10 rounded-lg px-4 py-2">
                          <div className="space-y-0.5">
                            <FormLabel className="whitespace-nowrap text-base">{role.name}</FormLabel>
                            {role.description && (
                              <div className="text-xs text-muted-foreground">{role.description}</div>
                            )}
                          </div>
                          <FormControl>
                            <Switch
                              checked={!!field.value}
                              onCheckedChange={field.onChange}
                              disabled={!watchUserId && !selectedUser}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="form-action-container">
              <Button type="submit">{selectedUser ? 'Save' : 'Add User'}</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
